import { useTranslation } from 'react-i18next';
import { PURGE_STORES } from '@airclou/shared';
import type { PreviewStore, StorePreview, TenantErasurePreview } from '../../../services/api';

/**
 * "What will be erased", app by app.
 *
 * Rendered twice from one component on purpose: the delete modal shows it
 * BEFORE the owner confirms, and the deleted modal shows it again afterwards as
 * the receipt. Two copies of this list would drift, and the pair that must
 * agree most is precisely "what we told you would go" and "what we are going to
 * take" (docs/settings-open-items.md §7).
 */

const STORE_NAME_KEY: Record<PreviewStore, string> = {
  knowledgebase: 'deleteOrg.storeKnowledgebase',
  helpdesk: 'deleteOrg.storeHelpdesk',
  playbook: 'deleteOrg.storePlaybook',
  forms: 'deleteOrg.storeForms',
  stripe: 'deleteOrg.storeStripe',
  auth: 'deleteOrg.storeAuth',
  dashboard: 'deleteOrg.storeDashboard',
};

interface ErasureStoreListProps {
  /** Null while loading, or when the summary could not be drawn at all. */
  preview: TenantErasurePreview | null;
  isLoading?: boolean;
}

export function ErasureStoreList({ preview, isLoading = false }: ErasureStoreListProps) {
  const { t, i18n } = useTranslation('dashboard');
  const formatCount = (n: number) => new Intl.NumberFormat(i18n.language || undefined).format(n);

  // The raw error never reaches the screen: it names hosts and tables the owner
  // cannot act on. "Could not be counted" is the whole of what they can use.
  const describe = (store: PreviewStore, row: StorePreview | undefined): { text: string; muted: boolean } => {
    // `undefined` should not happen — the server fills every store — but this
    // is a screen about losing everything, and a thrown render would leave the
    // owner with nothing at all.
    if (!row || !row.ok) return { text: t('deleteOrg.couldNotCount'), muted: true };
    if (store === 'stripe') {
      return row.detail.outcome === 'would_delete'
        ? { text: t('deleteOrg.stripeCustomerRecord'), muted: false }
        : { text: t('deleteOrg.stripeNoRecord'), muted: true };
    }
    if (row.rows === 0) return { text: t('deleteOrg.nothingStored'), muted: true };
    return { text: t('deleteOrg.records', { count: row.rows, n: formatCount(row.rows) }), muted: false };
  };

  // A store that did not answer is named as unanswered, never folded into the
  // others as a zero — a total that quietly omits a store is worse than no
  // total, because it reads as reassurance.
  const unanswered = preview
    ? PURGE_STORES.filter((s) => {
        const row = preview.stores[s];
        return !row || !row.ok;
      }).length
    : 0;

  return (
    <div>
      <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
        {PURGE_STORES.map((store) => {
          const { text, muted } = describe(store, preview?.stores[store]);
          return (
            <li key={store} className="flex items-center justify-between gap-4 px-3 py-2" data-store={store}>
              <span className="text-gray-700 dark:text-gray-300">{t(STORE_NAME_KEY[store])}</span>
              {isLoading ? (
                // Only the figure waits. The store names are known before any
                // request and paint immediately, so the list has its full shape
                // from the first frame and does not jump when the counts land.
                <span className="h-4 w-20 animate-pulse rounded bg-muted" aria-hidden="true" />
              ) : (
                <span
                  className={
                    muted ? 'text-gray-500 dark:text-gray-400' : 'font-medium text-foreground'
                  }
                >
                  {text}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {!isLoading && unanswered > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('deleteOrg.someStoresUnavailable')}</p>
      )}
    </div>
  );
}
